import React, { useState, useEffect, useCallback } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, TextField, CircularProgress, TablePagination, Box, MenuItem, Select, InputLabel, FormControl, Grid, SelectChangeEvent, Button, Checkbox, FormControlLabel, DialogActions } from '@mui/material';
import { Visibility, Update, CheckBox, Search, Edit } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import { estadoMisServicios, estadoServicios } from './types';
import moment from 'moment';
import { useAuth } from '../../context/auth';
import { AccessDenied } from '../../components/AccessDenied/Index';
import { getMisServiciosById, getServiciosFiltrados, updateEstadoMisServicios } from '../../api/misServicios';
import ViewLiquidacion from './_ViewById';
import { ConfirmTerminar } from '../../components/ConfirmFinalizar';
import ReportNove from './_ReportNove';

const MisServicios = () => {
    const [misServicios, setMisServicios] = useState<any[]>([]);
    const [openView, setOpenView] = useState(false);
    const [openNota, setOpenNota] = useState(false);
    const [selectedServicios, setSelectedServicios] = useState(null);
    const [loading, setLoading] = useState(true);
    const [estadoFilter, setEstadoFilter] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [isOpenC, setIsOpenC] = useState(false);
    const [busquedaNumServ, setBusquedaNumServ] = useState('');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [loadingEstado, setLoadingEstado] = useState<number | null>(null);

    const [openConfirmCompletar, setOpenConfirmCompletar] = useState(false);
    const [completarId, setCompletarId] = useState<number | null>(null);
    const [completarEstado, setCompletarEstado] = useState<number | null>(null);
    const [filters, setFilters] = useState({
        startDate: moment().subtract(1, 'months'),
        endDate: moment().add(1, 'months'),
    });

    const { permisos, user } = useAuth();
    const { Prm_MisServicios_Ver, Prm_MisServicios_Modificar } = permisos;


    const handleOpenDeleteConfirm = useCallback((id: number, estado: number) => {
        setCompletarId(id);
        setCompletarEstado(estado);
        setOpenConfirmCompletar(true);
    }, []);

    const handleCloseDeleteConfirm = useCallback(() => {
        setOpenConfirmCompletar(false);
        setCompletarId(null);
        setCompletarEstado(null);
    }, []);

    const handleConfirmCompletar = useCallback(
        async (cantidad: number | null) => {
            setLoadingEstado(completarId);
            if (completarId !== null) {
                const response = await updateEstadoMisServicios(completarId, completarEstado, cantidad);
                setLoadingEstado(null);
                if (response.isOk) {
                    setMisServicios((prevServ) =>
                        prevServ.map((servicios: any) =>
                            servicios.SrvDI_Id === completarId
                                ? { ...servicios, SrvDI_Estado: completarEstado }
                                : servicios
                        )
                    );
                }
                if (user?.Us_In_Id) {
                    fetchMisServicios(user.Us_In_Id);
                }
                handleCloseDeleteConfirm();
            }
        },
        [completarId, completarEstado, handleCloseDeleteConfirm]
    );


    // const handleEstadoChange = useCallback(async (id: number, newEstado: string) => {
    //     setLoadingEstado(id);
    //     const response = await updateEstadoMisServicios(id, parseInt(newEstado));
    //     setLoadingEstado(null);
    //     if (response.isOk) {
    //         setMisServicios((prevServ) => prevServ.map((servicios: any) => (servicios.SrvDI_Id === id ? { ...servicios, SrvDI_Estado: parseInt(newEstado) } : servicios)));
    //     }
    // }, []);

    const handleEstadoFilterChange = useCallback((e: SelectChangeEvent<string[]>) => {
        setEstadoFilter(e.target.value as string[]);
    }, []);

    const handleStartDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setStartDate(e.target.value);
    }, []);

    const handleEndDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEndDate(e.target.value);
    }, []);

    useEffect(() => {
        if (user?.Us_In_Id) {
            fetchMisServicios(user.Us_In_Id);
        }
    }, [user]);

    const fetchMisServicios = useCallback(async (id: number) => {
        setLoading(true);
        const response = await getMisServiciosById(id);
        if (response.isOk) {
            const serviciosFiltrados = response.data.filter((servicio: any) => {
                const fechaServicio = moment(servicio.Srv_FechaServicio);
                return fechaServicio.isBetween(filters.startDate, filters.endDate, 'day', '[]');
            });
            setMisServicios(serviciosFiltrados);
        } else {
            setMisServicios([]); // En caso de error o datos indefinidos, establecer un arreglo vacío
        }
        setLoading(false);
    }, [filters]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    }, []);

    const handleChangePage = useCallback((event: unknown, newPage: number) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);


    const handleOpenFil = () => {
        setIsOpenC(true);
    };

    const handleCloseFil = () => {
        setIsOpenC(false);
    }

    const handleFiltrarServicios = async () => {
        const filtros: any = {};
        if (user?.Us_In_Id) filtros.id = user.Us_In_Id;
        if (busquedaNumServ) filtros.numero_servicio = busquedaNumServ;
        if (fechaInicio) filtros.fecha_inicio = fechaInicio;
        if (fechaFin) filtros.fecha_fin = fechaFin;

        setLoading(true);
        const response = await getServiciosFiltrados(filtros);
        if (response.isOk) {
            setMisServicios(response.data);
            setBusquedaNumServ('');
            setFechaInicio('');
            setFechaFin('');
        } else {
            alert('No se ha encontrado');
        }
        setLoading(false);
        setIsOpenC(false);
    };

    const filteredmisServicios = misServicios.filter((servicios: any) => {
        const misServiciosFechaServicio = moment(servicios.Srv_FechaServicio).startOf('day');
        const startDateNormalized = startDate ? moment(startDate).startOf('day') : null;
        const endDateNormalized = endDate ? moment(endDate).endOf('day') : null;

        const matchesSearchTerm = servicios.Srv_VehiculoPlacaOSerial.toLowerCase().includes(searchTerm.toLowerCase()) || (servicios.SrvDI_Detalle || '').toLowerCase().includes(searchTerm.toLowerCase()) || servicios.Pr_Nombre.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesEstadoFilter = estadoFilter.length === 0 || estadoFilter.includes(servicios.SrvDI_Estado.toString());
        const matchesStartDate = startDateNormalized === null || misServiciosFechaServicio.isSameOrAfter(startDateNormalized);
        const matchesEndDate = endDateNormalized === null || misServiciosFechaServicio.isSameOrBefore(endDateNormalized);

        return matchesSearchTerm && matchesEstadoFilter && matchesStartDate && matchesEndDate;
    });

    const getEstadoText = (id: string) => {
        const estado = estadoMisServicios.find((e) => e.id.toString() === id);
        return estado ? estado.texto : 'Todos';
    };

    const calculateTotalValue = (servicios: any[]) => {
        return servicios.reduce((total, servicio) => {
            const valor = parseFloat(servicio.SrvDI_Valor) || 0;
            return total + valor;
        }, 0);
    };

    if (user?.Us_In_Id === 0 || Prm_MisServicios_Ver === 0) {
        return <AccessDenied />;
    }

    const handleActualizarClick = () => {
        if (user?.Us_In_Id) {
            fetchMisServicios(user.Us_In_Id);
        }
    };

    return (
        <div style={{ marginTop: '5px' }}>
            {loading ? (
                <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                    <CircularProgress />
                </Box>
            ) : (
                <div style={{ marginTop: '5px' }}>
                    <Grid container spacing={2} alignItems='center' mb={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField label='Buscar' value={searchTerm} onChange={handleSearchChange} fullWidth size='small' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl variant='outlined' size='small' fullWidth>
                                <InputLabel>Estado</InputLabel>
                                <Select
                                    multiple
                                    value={estadoFilter}
                                    onChange={handleEstadoFilterChange}
                                    label='Estado'
                                    renderValue={(selected) => {
                                        // Mostrar los nombres de los estados seleccionados
                                        return selected.map((id) => getEstadoText(id)).join(', ');
                                    }}>
                                    {estadoMisServicios.map((estado) => (
                                        <MenuItem key={estado.id} value={estado.id.toString()}>
                                            {estado.texto}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField label='Fecha de inicio' type='date' value={startDate || ''} onChange={handleStartDateChange} InputLabelProps={{ shrink: true }} fullWidth size='small' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField label='Fecha fin' type='date' value={endDate || ''} onChange={handleEndDateChange} InputLabelProps={{ shrink: true }} fullWidth size='small' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                            <IconButton onClick={handleOpenFil} title='Abrir' className='tool-btn'>
                                <Search />
                            </IconButton>
                            <IconButton onClick={handleActualizarClick} title='Actualizar' className='tool-btn'>
                                <Update />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <TableContainer component={Paper}>
                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Servicio</TableCell>
                                    <TableCell>Fecha de servicio</TableCell>
                                    <TableCell>Estado</TableCell>
                                    <TableCell>Placa/Serie</TableCell>
                                    <TableCell>Detalle</TableCell>
                                    <TableCell>Producto</TableCell>
                                    <TableCell>Valor</TableCell>
                                    <TableCell colSpan={3}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredmisServicios.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((servicios: any) => (
                                    <TableRow key={servicios.SrvDI_Id}>
                                        <TableCell>{servicios.Srv_Id}</TableCell>
                                        <TableCell>{moment(servicios.Srv_FechaServicio).format('YYYY-MM-DD')}</TableCell>
                                        <TableCell>
                                            {loadingEstado === servicios.SrvDI_Id ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                <span style={{ padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, display: 'inline-block', backgroundColor: estadoMisServicios.find((estado) => estado.id === parseInt(servicios.SrvDI_Estado))?.color }}>{estadoMisServicios.find(e => e.id === servicios.SrvDI_Estado)?.texto}</span>
                                            )}
                                        </TableCell>
                                        <TableCell>{servicios.Srv_VehiculoPlacaOSerial}</TableCell>
                                        <TableCell>{servicios.SrvDI_Detalle}</TableCell>
                                        <TableCell>{servicios.Pr_Nombre}</TableCell>
                                        <TableCell>{servicios.SrvDI_Valor}</TableCell>
                                        <TableCell colSpan={3}>
                                            <IconButton
                                                onClick={() => {
                                                    setSelectedServicios(servicios);
                                                    setOpenView(true);
                                                }}>
                                                <Visibility color='info' />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => {
                                                    setSelectedServicios(servicios);
                                                    setOpenNota(true);
                                                }}>
                                                <WarningIcon color='warning' />
                                            </IconButton>
                                            {![2, 3, 4].includes(servicios.SrvDI_Estado) && (
                                                <Checkbox
                                                    color="primary"
                                                    onChange={() => handleOpenDeleteConfirm(servicios.SrvDI_Id, 2)}
                                                />
                                                // <IconButton onClick={() => { handleOpenDeleteConfirm(servicios.SrvDI_Id, 2); }}>
                                                //     <CheckBox />
                                                // </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {/* Fila para el total */}
                                <TableRow>
                                    <TableCell colSpan={5} align='right'>
                                        <strong>Total:</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>{calculateTotalValue(filteredmisServicios)}</strong>
                                    </TableCell>
                                    <TableCell colSpan={3} />
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination component='div' count={filteredmisServicios.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
                </div>
            )}

            <Dialog open={openNota} onClose={() => setOpenNota(false)} maxWidth='md' fullWidth>
                <DialogTitle>Reportar novedades</DialogTitle>
                <DialogContent>
                    <ReportNove
                        servicios={selectedServicios}
                        onClose={() => {
                            setOpenNota(false);
                            if (user?.Us_In_Id) {
                                fetchMisServicios(user.Us_In_Id);
                            }
                        }}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={openView} onClose={() => setOpenView(false)}>
                <DialogTitle>Ver servicios</DialogTitle>
                <DialogContent>
                    <ViewLiquidacion servicios={selectedServicios} onClose={() => setOpenView(false)} />
                </DialogContent>
            </Dialog>

            <ConfirmTerminar open={openConfirmCompletar} onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmCompletar} message='¿Estás seguro de dar como terminado este servicio?' />

            <Dialog open={isOpenC} fullWidth maxWidth='xs'>
                <DialogContent>
                    <Grid item marginBottom="15px">
                        <TextField label='Servicio' value={busquedaNumServ} onChange={(e) => setBusquedaNumServ(e.target.value)} fullWidth size='small' />
                    </Grid>
                    <Grid item >
                        <TextField label='Fecha Inicio' type='date' value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size='small' />
                    </Grid>
                    <Grid item marginTop="15px">
                        <TextField label='Fecha Fin' type='date' value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size='small' />
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseFil}>Cerrar</Button>
                    <Button onClick={handleFiltrarServicios} variant="contained" color="primary">Filtrar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default MisServicios;
